/**
 * Subscription Service
 * 
 * Connects to Firebase Cloud Functions for Stripe integration.
 * Falls back to Firestore for status checks.
 */

import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '@/firebase/config';

export interface SubscriptionData {
  premiumStatus: 'free' | 'trial' | 'premium' | 'canceling';
  trialStartedAt?: number;
  trialEndsAt?: number;
  hasUsedTrial?: boolean;
  subscribedAt?: number;
  subscriptionId?: string;
  stripeCustomerId?: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  // Lifetime free-tier counter (incremented on every Price Check save, never decremented)
  priceChecksUsed?: number;
}

/**
 * Start Stripe Checkout for subscription.
 * Cloud Function attaches a 7-day trial server-side if the user is trial-eligible
 * (see functions/src/stripe.ts). Backing out of Checkout writes nothing to Firestore.
 */
export async function startCheckout(): Promise<void> {
  const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

  const result = await createCheckoutSession({
    successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${window.location.origin}/billing/canceled`,
  });

  const { url } = result.data as { sessionId: string; url: string };

  // Redirect to Stripe Checkout
  if (url) {
    window.location.href = url;
  }
}

/**
 * Subscribe to user's subscription status changes
 */
export function subscribeToStatus(
  userId: string,
  callback: (data: SubscriptionData) => void
): () => void {
  const userRef = doc(db, 'users', userId);

  return onSnapshot(userRef, (snapshot) => {
    const rawData = snapshot.data();
    if (!rawData || !rawData.premiumStatus) {
      callback({ premiumStatus: 'free' });
      return;
    }
    callback(rawData as SubscriptionData);
  });
}

/**
 * Check if user has premium access
 */
export function hasPremiumAccess(data: SubscriptionData): boolean {
  const status = data.premiumStatus;

  // Check trial expiry
  if (status === 'trial' && data.trialEndsAt) {
    if (Date.now() > data.trialEndsAt) {
      return false;
    }
  }

  return status === 'trial' || status === 'premium' || status === 'canceling';
}

/**
 * Get days remaining in trial
 */
export function getTrialDaysRemaining(data: SubscriptionData): number | null {
  if (data.premiumStatus !== 'trial' || !data.trialEndsAt) return null;
  
  const diffMs = data.trialEndsAt - Date.now();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
}

/**
 * Open Stripe Customer Portal for subscription management
 */
export async function openCustomerPortal(): Promise<void> {
  const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');

  const result = await createPortalSession({
    returnUrl: `${window.location.origin}/app/studio`,
  });

  const { url } = result.data as { url: string };

  if (url) {
    window.location.href = url;
  }
}